import React, { useEffect, useState, useContext } from "react";
import {
  Table,
  Alert,
  Container,
  Card,
  Button,
  Badge,
  Form,
  Row,
  Col,
} from "react-bootstrap";
import { InputGroup, InputGroupText, Input, FormGroup } from "reactstrap";
import SimpleHeader from "components/Headers/SimpleHeader.js";
import { ThemeContext } from "contexts/ThemeContext.jsx";
import { serverHost } from "variables/serverdata";
import { hasPermission, PERMISSIONS } from "utils/permissions";
import HusariaHeader from "views/pages/components/HusariaHeader";
import paginationFactory from "react-bootstrap-table2-paginator";
import BootstrapTable from "react-bootstrap-table-next";

const ExtensionSummary = () => {
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";
  const userData = React.useMemo(() => {
    const storedUser = localStorage.getItem("user");
    return storedUser ? JSON.parse(storedUser) : null;
  }, []);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateRange, setDateRange] = useState("7");
  const [includeInternal, setIncludeInternal] = useState(true);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const visibleColumns = [
    { key: "extension", label: "Extension" },
    { key: "answered", label: "Answered" },
    { key: "missed", label: "Missed" },
    { key: "no_answer", label: "No Answer" },
    { key: "busy", label: "Busy" },
    { key: "voicemail", label: "Voicemail" },
    { key: "aloc", label: "ALOC" },
    { key: "inbound_calls", label: "Inbound Calls" },
    { key: "inbound_duration", label: "Inbound Duration" },
    { key: "outbound_calls", label: "Outbound Calls" },
    { key: "outbound_duration", label: "Outbound Duration" },
    { key: "description", label: "Description" },
  ];
  const rowKey = "extension";
  const fetchExtensions = async () => {
    setLoading(true);
    setError(null);
    try {
      const site = JSON.parse(localStorage.getItem("selectedSite"));
      const user = JSON.parse(localStorage.getItem("user"));
      const payload = {
        internal: includeInternal,
        start: startDate,
        end: endDate,
        range: dateRange,
      };
      if (user.user_role === "USER") payload.user_uuid = user.user_uuid;
      const response = await fetch(
        `${serverHost}/extension_summary/${site.site_uuid}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        },
      );
      if (!response.ok) throw new Error("Failed to fetch extension statistics");
      const data = await response.json();
      console.log(data.result);
      setRows(data.result || []);
      console.log("Refreshing extension statistics...");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const columns = visibleColumns.map((col, index) => ({
    dataField: col.key,
    text: col.label,
    sort: true,
    formatter: (cell, row) => {
      const badgeConfig = {
        1: { bg: "success" },
        2: { bg: "danger" },
        3: { bg: "info" },
        4: { bg: "info" },
        5: { bg: "info" },
      };

      if (index === 6 || index === 8 || index === 10) return formatToHMS(cell) || "0:00";
      if (index === 7 || index === 9) return cell || "0";
      const config = badgeConfig[index];
      if (config) {
        return (
          <Badge bg={config.bg} style={{ color: "black" }}>
            {cell || 0}
          </Badge>
        );
      }

      return cell || "-";
    },
  }));

  const pagination = paginationFactory({
    page: 1,
    alwaysShowAllBtns: true,
    showTotal: true,
    withFirstAndLast: false,
    sizePerPageRenderer: ({ onSizePerPageChange }) => (
      <div className="dataTables_length">
        <label>
          Show{" "}
          <select
            className="form-control form-control-sm"
            onChange={(e) => onSizePerPageChange(e.target.value)}
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>{" "}
          entries.
        </label>
      </div>
    ),
  });

  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc", // "asc" | "desc"
  });

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      return { key, direction: "asc" };
    });
  };

  const filteredRows = React.useMemo(() => {
    if (!searchTerm) return rows;
    const term = searchTerm.toLowerCase();
    return rows.filter(
      (ext) =>
        ext.extension?.toLowerCase().includes(term) ||
        ext.description?.toLowerCase().includes(term),
    );
  }, [rows, searchTerm, visibleColumns]);

  useEffect(() => {
    fetchExtensions();
  }, []);

  const handleApplyFilters = () => {
    fetchExtensions();
  };

  function formatToHMS(secondsFloat) {
    const totalSeconds = Math.floor(secondsFloat); // remove decimal part

    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return [
      hours.toString().padStart(2, "0"),
      minutes.toString().padStart(2, "0"),
      seconds.toString().padStart(2, "0"),
    ].join(":");
  }

  const downloadCSV = () => {
    const headers = [
      "Extension",
      "Answered",
      "Missed",
      "Voicemail",
      "No Answer",
      "Busy",
      "ALOC",
      "Inbound Calls",
      "Inbound Duration",
      "Outbound Calls",
      "Outbound Duration",
      "Description",
    ];

    const rows = filteredRows.map((ext) => [
      ext.extension || "",
      ext.answered || 0,
      ext.missed || 0,
      ext.voicemail || 0,
      ext.no_answer || 0,
      ext.busy || 0,
      ext.aloc || "0:00",
      ext.inbound_calls || 0,
      ext.inbound_duration || "0:00",
      ext.outbound_calls || 0,
      ext.outbound_duration || "0:00",
      ext.description || "",
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `extension_stats_${new Date().toISOString().split("T")[0]}.csv`,
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleReset = () => {
    setDateRange("today");
    setIncludeInternal(true);
    setStartDate("");
    setEndDate("");
    setSearchTerm("");
  };

  return (
    <>
      <SimpleHeader name="Extension Statistics" parentName="HUCS" />

      <Container fluid className="mt--6">
        {/* Filters Card */}
        <Card
          className={`shadow-sm mb-3 ${isDark ? "bg-dark text-light border-dark" : ""}`}
          style={{ borderRadius: "1rem" }}
        >
          <Card.Header
            style={{
              backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
              color: isDark ? "#e2e8f0" : "rgb(17, 17, 17)",
              borderTopLeftRadius: "1rem",
              borderTopRightRadius: "1rem",
              overflow: "hidden",
            }}
          >
            <h5 className="mb-0">Filters</h5>
          </Card.Header>
          <Card.Body
            style={{
              backgroundColor: isDark ? "#1f2937" : "#fff",
              borderBottomLeftRadius: "1rem",
              borderBottomRightRadius: "1rem",
            }}
          >
            <Row className="g-3">
              <Col md={3}>
                <Form.Label style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
                  Time Range
                </Form.Label>
                <Input
                  type="select"
                  value={dateRange}
                  onChange={(e) => setDateRange(e.target.value)}
                  style={{
                    backgroundColor: isDark ? "#374151" : "#fff",
                    color: isDark ? "#e2e8f0" : "#212529",
                    borderColor: isDark ? "#4b5563" : "#ced4da",
                  }}
                >
                  <option value="1">Last 7 Days</option>
                  <option value="2">Last Hour</option>
                  <option value="3">Today</option>
                  <option value="4">Yesterday</option>
                  <option value="5">This Week</option>
                  <option value="6">This Month</option>
                  <option value="7">This Year</option>
                  <option value="custom">Custom Range</option>
                </Input>
              </Col>

              <Col md={3}>
                <Form.Label style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
                  Start Date
                </Form.Label>
                <Form.Control
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  disabled={dateRange !== "custom"}
                  style={{
                    backgroundColor: isDark ? "#374151" : "#fff",
                    color: isDark ? "#e2e8f0" : "#212529",
                    borderColor: isDark ? "#4b5563" : "#ced4da",
                  }}
                />
              </Col>

              <Col md={3}>
                <Form.Label style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
                  End Date
                </Form.Label>
                <Form.Control
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  disabled={dateRange !== "custom"}
                  style={{
                    backgroundColor: isDark ? "#374151" : "#fff",
                    color: isDark ? "#e2e8f0" : "#212529",
                    borderColor: isDark ? "#4b5563" : "#ced4da",
                  }}
                />
              </Col>

              <Col md={3} className="d-flex align-items-end">
                <Form.Check
                  type="checkbox"
                  id="include-internal"
                  label="Include Internal Calls"
                  checked={includeInternal}
                  onChange={(e) => setIncludeInternal(e.target.checked)}
                  style={{ color: isDark ? "#e2e8f0" : "#212529" }}
                />
              </Col>
            </Row>

            <Row className="mt-3">
              <Col>
                <Button
                  variant="primary"
                  onClick={handleApplyFilters}
                  disabled={loading}
                  className="me-2"
                >
                  {loading ? "Loading..." : "Apply Filters"}
                </Button>
                <Button variant="light" onClick={handleReset}>
                  Reset
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Statistics Table */}
        <Card
          className={`shadow-sm ${isDark ? "bg-dark text-light border-dark" : ""}`}
        >
          <Card.Header
            className="d-flex justify-content-between align-items-center flex-wrap"
            style={{
              backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
              color: isDark ? "#e2e8f0" : "#111",
            }}
          >
            <div className="d-flex align-items-center">
              <h4 className="mb-2 mb-md-0">
                <Badge
                  bg="primary"
                  className="ms-2"
                  style={{ marginLeft: "0.5rem", marginRight: "1rem" }}
                >
                  {filteredRows.length} Extensions
                </Badge>
              </h4>
              <Form className="navbar-search navbar-search-light form-inline search-border">
                <FormGroup className="mb-0">
                  <InputGroup className="input-group-alternative input-group-merge">
                    <InputGroupText>
                      <i
                        className="fas fa-search"
                        style={{
                          color: isDark ? "#b0b0b0" : "rgb(102, 102, 102)",
                        }}
                      />
                    </InputGroupText>
                    <Input
                      className="table-search-bar form-control "
                      placeholder="Search"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </InputGroup>
                </FormGroup>
              </Form>
            </div>
            <div className="d-flex gap-2 flex-wrap align-items-center">
              <Button
                variant="success"
                size="sm"
                onClick={downloadCSV}
                disabled={filteredRows.length === 0}
              >
                Download CSV
              </Button>
              <Button
                variant="info"
                size="sm"
                onClick={fetchExtensions}
                disabled={loading}
              >
                {loading ? "Refreshing..." : "Refresh"}
              </Button>
            </div>
          </Card.Header>

          <Card.Body
            style={{ padding: 0, backgroundColor: isDark ? "#1f2937" : "#fff" }}
          >
            {error ? (
              <Alert variant="danger" className="m-3">
                {error}
              </Alert>
            ) : filteredRows.length === 0 ? (
              <div
                className="text-center py-5"
                style={{ color: isDark ? "#9ca3af" : "#6c757d" }}
              >
                <h5>No extension statistics found</h5>
                <p className="mb-0">
                  {searchTerm
                    ? "No extensions match your search criteria."
                    : "There are currently no statistics for the selected period."}
                </p>
              </div>
            ) : (

              <div>
              <div className="py-4 table-responsive">
                <BootstrapTable
                  keyField={rowKey}
                  data={filteredRows} // <-- use filteredRows instead of all rows
                  columns={columns}
                  bootstrap4
                  pagination={pagination}
                  bordered={false}
                  hover
                  classes="align-items-center table-flush"
                  noDataIndication={() =>
                    loading ? (
                      <div className="text-center">Loading...</div>
                    ) : (
                      <div className="text-center">No records found.</div>
                    )
                  }
                />
              </div>
            </div>
            )}
          </Card.Body>
        </Card>
      </Container>
    </>
  );
};

export default ExtensionSummary;
